import { Component, Input, Output, EventEmitter, signal, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConvenioRepository } from 'src/app/domain/service/convenio/convenio-repository';
import { Convenio, FiltroConvenio, ConvenioSeleccionado } from 'src/app/domain/model/convenio/convenio';
import { HttpConvenioRepository } from 'src/app/infrastructure/adapters/convenio/http-convenio.repository';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { sanitizeAlphanumeric, sanitizeText } from 'src/app/common/input-utils';

/**
 * Componente: Buscador de Convenios (Modal)
 * 
 * Diálogo de búsqueda de convenios con filtros opcionales.
 * 
 * Comportamiento:
 * - Si recibe nmconsecutivoCliente, pre-filtra por esa aseguradora
 * - Filtros: Código, Descripción, Activo (S/N/Todos)
 * - Tabla paginada con resultados
 * - Emite evento cuando se selecciona un convenio
 * 
 * Legacy: BuscarConveniosActivity.java + BuscarConveniosViewImpl.java
 * 
 * @author GitHub Copilot - Migración GIROP
 */
@Component({
  selector: 'app-busqueda-convenio-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    ToastModule,
    BusquedaPaginadorComponent
  ],
  providers: [
    MessageService,
    { provide: ConvenioRepository, useClass: HttpConvenioRepository }
  ],
  template: `
    <p-toast></p-toast>
    
    <p-dialog 
      header="Filtros Consulta de Convenios"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '53rem' }"
      [closable]="true"
      (onHide)="onCancelar()">
      
      <!-- Sección Filtros de Búsqueda -->
      <div class="border dark:border-2 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
        <div class="font-medium text-gray-700 dark:text-gray-200 mb-3 text-sm">Filtros de Búsqueda</div>
        
        <!-- Aseguradora (solo lectura si viene pre-filtrado) -->
        @if (filtro.nmconsecutivoCliente) {
          <div class="mb-3">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Aseguradora</label>
            <input pInputText 
              [value]="nombreCliente"
              [disabled]="true"
              class="w-full" />
          </div>
        }
        
        <!-- Código -->
        <div class="mb-3">
          <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Código</label>
          <input pInputText
            [(ngModel)]="filtro.cdconvenio"
            (input)="filtro.cdconvenio = sanitizeAlphanumeric(filtro.cdconvenio)"
            maxlength="100"
            placeholder="Ej: CONV-001"
            class="w-full" />
        </div>

        <!-- Descripción -->
        <div class="mb-3">
          <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Descripción</label>
          <input pInputText
            [(ngModel)]="filtro.dsconvenio"
            (input)="filtro.dsconvenio = sanitizeText(filtro.dsconvenio)"
            maxlength="100"
            placeholder="Búsqueda parcial"
            class="w-full" />
        </div>
        
        <!-- Activo -->
        <div class="mb-3">
          <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">Activo</label>
          <div class="flex gap-4 mt-2">
            <div class="flex items-center">
              <p-radioButton 
                name="activo" 
                value="S" 
                [(ngModel)]="filtro.snactivo" 
                inputId="activo_s" />
              <label for="activo_s" class="ml-2 cursor-pointer">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton 
                name="activo" 
                value="N" 
                [(ngModel)]="filtro.snactivo" 
                inputId="activo_n" />
              <label for="activo_n" class="ml-2 cursor-pointer">No</label>
            </div>
            <div class="flex items-center">
              <p-radioButton 
                name="activo" 
                [value]="null" 
                [(ngModel)]="filtro.snactivo" 
                inputId="activo_todos" />
              <label for="activo_todos" class="ml-2 cursor-pointer">Todos</label>
            </div>
          </div>
        </div>
        
        <!-- Botones Buscar/Limpiar -->
        <div class="flex flex-wrap gap-2 mt-4">
          <button pButton 
            label="Buscar" 
            icon="pi pi-search"
            (click)="buscar()"
            [disabled]="loading()"></button>
          <button pButton 
            label="Limpiar" 
            icon="pi pi-times"
            class="p-button-secondary"
            (click)="limpiarFiltros()"
            [disabled]="loading()"></button>
        </div>
      </div>
      
      <!-- Tabla de Resultados -->
      <div class="mb-3">
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Convenios</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            </span>
          }
        </div>

        @if (loading()) {
          <div class="flex justify-center items-center py-8">
            <p-progressSpinner 
              styleClass="w-12 h-12"
              strokeWidth="4"></p-progressSpinner>
          </div>
        } @else {
          <p-table
            [value]="conveniosPaginados()"
            [paginator]="false"
            [rows]="pageSize()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 20%">Código</th>
                <th style="width: 50%">Descripción</th>
                <th style="width: 15%">Activo</th>
                <th style="width: 15%">Seleccionar</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-convenio>
              <tr>
                <td>{{ convenio.cdconvenio }}</td>
                <td>{{ convenio.dsconvenio }}</td>
                <td>{{ convenio.snactivo === 'S' ? 'Sí' : 'No' }}</td>
                <td>
                  <a
                    class="text-blue-600 hover:underline cursor-pointer"
                    (click)="onConvenioSeleccionado(convenio)">
                    Seleccionar
                  </a>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="text-center py-4 text-gray-500">
                  No se encontraron convenios. Ajuste los filtros de búsqueda.
                </td>
              </tr>
            </ng-template>
          </p-table>

          @if (totalRegistros() > 0) {
            <app-busqueda-paginador
              [currentPage]="currentPage()"
              [totalPages]="totalPages()"
              [hasPrevious]="hasPrevious()"
              [hasNext]="hasNext()"
              [pageSizeOptions]="pageSizeOptions"
              [selectedPageSize]="pageSize()"
              (pageSizeChange)="onPageSizeChange($event)"
              (first)="irPrimeraPagina()"
              (previous)="irPaginaAnterior()"
              (next)="irPaginaSiguiente()"
              (last)="irUltimaPagina()"
            ></app-busqueda-paginador>
          }
        }
      </div>
      
      <ng-template pTemplate="footer">
        <button pButton 
          label="Cancelar" 
          icon="pi pi-times"
          class="p-button-text"
          (click)="onCancelar()"></button>
      </ng-template>
    </p-dialog>
  `
})
export class BuscadorConvenioDialogComponent implements OnInit {
  @Input() visible = false;
  @Input() nmconsecutivoCliente?: number;
  @Input() nombreCliente?: string;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() convenioSeleccionado = new EventEmitter<ConvenioSeleccionado>();
  
  convenios = signal<Convenio[]>([]);
  loading = signal(false);

  sanitizeAlphanumeric = sanitizeAlphanumeric;
  sanitizeText = sanitizeText;

  // Paginación manual (frontend-girop §17 / ERROR 6 - prohibido [paginator]="true")
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly totalRegistros = computed(() => this.convenios().length);
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()))
  );
  readonly conveniosPaginados = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.convenios().slice(start, start + this.pageSize());
  });
  readonly hasPrevious = computed(() => this.currentPage() > 1);
  readonly hasNext = computed(() => this.currentPage() < this.totalPages());
  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });
  readonly registroFin = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.totalRegistros())
  );

  irPrimeraPagina(): void {
    this.currentPage.set(1);
  }
  irUltimaPagina(): void {
    this.currentPage.set(this.totalPages());
  }
  irPaginaAnterior(): void {
    if (this.hasPrevious()) this.currentPage.update((p) => p - 1);
  }
  irPaginaSiguiente(): void {
    if (this.hasNext()) this.currentPage.update((p) => p + 1);
  }
  onPageSizeChange(nuevoTamanio: number): void {
    this.pageSize.set(nuevoTamanio);
    this.currentPage.set(1);
  }


  filtro: FiltroConvenio = {
    snactivo: 'S'  // Por defecto, solo activos
  };
  
  private readonly convenioRepository = inject(ConvenioRepository);
  private readonly messageService = inject(MessageService);
  
  ngOnInit() {
    if (this.nmconsecutivoCliente) {
      this.filtro.nmconsecutivoCliente = this.nmconsecutivoCliente;
      this.buscar();  // Auto-buscar si viene pre-filtrado
    }
  }
  
  async buscar() {
    this.loading.set(true);
    
    try {
      const resultados = await this.convenioRepository.buscarConvenios(this.filtro);
      this.convenios.set(resultados);
      this.currentPage.set(1);

      if (resultados.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'Sin resultados',
          detail: 'No se encontraron convenios con los criterios especificados.'
        });
      }
    } catch (error) {
      console.error('Error buscando convenios:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al buscar convenios. Por favor intente nuevamente.'
      });
    } finally {
      this.loading.set(false);
    }
  }
  
  limpiarFiltros() {
    this.filtro = {
      nmconsecutivoCliente: this.nmconsecutivoCliente,  // Mantener pre-filtro
      snactivo: 'S'
    };
    this.convenios.set([]);
    this.currentPage.set(1);
  }
  
  onConvenioSeleccionado(convenio: Convenio) {
    
    const seleccionado: ConvenioSeleccionado = {
      nmconsecutivo: convenio.nmconsecutivo,
      cdconvenio: convenio.cdconvenio,
      dsconvenio: convenio.dsconvenio,
      dstipoFacturacion: convenio.dstipoFacturacion
    };
    
    this.convenioSeleccionado.emit(seleccionado);
    this.visible = false;
    this.visibleChange.emit(false);
  }
  
  onCancelar() {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
